// app/frontend/pages/Videos/VideoShow.tsx
import { useState } from "react";
import {
  Stack,
  Group,
  ActionIcon,
  Grid,
  AspectRatio,
  Paper,
  Divider,
  Box,
} from "@mantine/core";
import {
  IconChevronRight,
  IconHeart,
  IconHeartFilled,
  IconBookmark,
  IconBookmarkFilled,
  IconClock,
  IconCategory,
} from "@tabler/icons-react";
import { Link, usePage } from "@inertiajs/react";
import {
  L2KH2,
  L2KH4,
  L2KH5,
  L2KText,
  L2KCaption,
  L2KBadge,
} from "../../components/ui/Typography";
import type { L2KBadgeVariant } from "../../components/ui/Typography/types";
import VideoCard from "../../components/videos/VideoCard";
import HybridLayout from "../../components/layouts/HybridLayout";
import PageContainer from "../../components/layouts/PageContainer";
import { SEO } from "../../components/common/SEO";
import { VideoSubscriptionGate } from "../../components/subscription";
import { ContentActionsMenu } from "../../components/ui/ContentActionsMenu";
import { ComingSoonPageOverlay } from "../../components/videos/ComingSoonPageOverlay";
import type { Video, UserVideo } from "../../types/video";

interface Props {
  video: Video;
  user_video: UserVideo | null;
  related_videos: Video[];
  vimeo_embed_url: string | null;
  track_name?: string;
  track_id?: number;
  can_watch: boolean;
  subscription_required?: boolean;
}

export default function VideoShow({
  video,
  user_video,
  related_videos,
  vimeo_embed_url,
  track_name,
  track_id,
  can_watch,
}: Props) {
  const [favorited, setFavorited] = useState(!!user_video?.favorited_at);
  const [bookmarked, setBookmarked] = useState(!!user_video?.bookmarked_at);

  const getSkillLevelVariant = (level: string): L2KBadgeVariant => {
    if (["beginner", "improver", "intermediate", "advanced"].includes(level)) {
      return level as L2KBadgeVariant;
    }
    return "muted";
  };

  const handleFavoriteToggle = async () => {
    try {
      const response = await fetch(`/videos/${video.slug}/toggle_favorite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });
      if (response.ok) {
        setFavorited(!favorited);
      }
    } catch (error) {
      console.error("Failed to toggle favorite:", error);
    }
  };

  const handleBookmarkToggle = async () => {
    try {
      const response = await fetch(`/videos/${video.slug}/toggle_bookmark`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });
      if (response.ok) {
        setBookmarked(!bookmarked);
      }
    } catch (error) {
      console.error("Failed to toggle bookmark:", error);
    }
  };

  const handleRelatedVideoClick = (relatedVideo: Video) => {
    window.location.href = `/videos/${relatedVideo.slug}`;
  };

  const { coming_soon } = usePage().props as { coming_soon?: boolean };

  const pageBody = (
    <PageContainer>
      <Stack gap="lg" py="md">
          {/* Breadcrumbs */}
          <Group gap="xs">
            <Link
              href="/dashboard"
              style={{
                textDecoration: "none",
                color: "#B8B8B8",
                fontSize: "clamp(12px, calc(-0.50px + 0.78vw), 14px)",
                fontFamily: "'Public Sans', sans-serif",
                transition: "color 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#FF9800")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#B8B8B8")}
            >
              Dashboard
            </Link>
            <IconChevronRight size={14} color="#666" />
            <Link
              href="/videos"
              style={{
                textDecoration: "none",
                color: "#B8B8B8",
                fontSize: "clamp(12px, calc(-0.50px + 0.78vw), 14px)",
                fontFamily: "'Public Sans', sans-serif",
                transition: "color 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#FF9800")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#B8B8B8")}
            >
              Videos
            </Link>
            {track_name && track_id && (
              <>
                <IconChevronRight size={14} color="#666" />
                <Link
                  href={`/videos?track_id=${track_id}`}
                  style={{
                    textDecoration: "none",
                    color: "#B8B8B8",
                    fontSize: "clamp(12px, calc(-0.50px + 0.78vw), 14px)",
                    fontFamily: "'Public Sans', sans-serif",
                    transition: "color 0.2s ease",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "#FF9800")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "#B8B8B8")}
                >
                  {track_name}
                </Link>
              </>
            )}
            <IconChevronRight size={14} color="#666" />
            <span
              style={{
                color: "#F0F0F0",
                fontSize: "clamp(12px, calc(-0.50px + 0.78vw), 14px)",
                fontFamily: "'Public Sans', sans-serif",
              }}
            >
              {video.title.length > 40 ? `${video.title.substring(0, 40)}...` : video.title}
            </span>
          </Group>

          {/* Video Player */}
          <VideoSubscriptionGate
            hasAccess={can_watch}
            thumbnailUrl={video.thumbnail_url}
            videoTitle={video.title}
          >
            <Paper
              radius="md"
              style={{
                overflow: "hidden",
                background: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
              }}
            >
              <AspectRatio ratio={16 / 9}>
                {vimeo_embed_url ? (
                  <iframe
                    src={vimeo_embed_url}
                    style={{
                      border: "none",
                      width: "100%",
                      height: "100%",
                    }}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    title={video.title}
                  />
                ) : (
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#000",
                    }}
                  >
                    <L2KText variant="secondary">Video unavailable</L2KText>
                  </Box>
                )}
              </AspectRatio>
            </Paper>
          </VideoSubscriptionGate>

          {/* Video Info */}
          <Box>
            <Group justify="space-between" align="flex-start" mb="md">
              <Box style={{ flex: 1 }}>
                <L2KH2 mb="xs">{video.title}</L2KH2>
                <Group gap="md">
                  <Group gap="xs">
                    <IconClock size={16} color="#888" />
                    <L2KCaption variant="tertiary">{video.duration}</L2KCaption>
                  </Group>
                  <L2KBadge variant={getSkillLevelVariant(video.skill_level)} size="md">
                    {video.skill_level}
                  </L2KBadge>
                </Group>
              </Box>

              {/* Action Buttons */}
              <Group gap="xs">
                <ActionIcon
                  variant="subtle"
                  color={favorited ? "red" : "gray"}
                  size="xl"
                  onClick={handleFavoriteToggle}
                  aria-label={
                    favorited ? "Remove from favorites" : "Add to favorites"
                  }
                >
                  {favorited ? (
                    <IconHeartFilled size={24} />
                  ) : (
                    <IconHeart size={24} />
                  )}
                </ActionIcon>
                <ActionIcon
                  variant="subtle"
                  color={bookmarked ? "blue" : "gray"}
                  size="xl"
                  onClick={handleBookmarkToggle}
                  aria-label={bookmarked ? "Remove bookmark" : "Add bookmark"}
                >
                  {bookmarked ? (
                    <IconBookmarkFilled size={24} />
                  ) : (
                    <IconBookmark size={24} />
                  )}
                </ActionIcon>
                <ContentActionsMenu
                  editUrl={`/admin/videos/${video.id}/edit`}
                  copyLabel="Copy video link"
                  ariaLabel="Video actions"
                />
              </Group>
            </Group>

            {/* Categories */}
            {video.categories && video.categories.length > 0 && (
              <Group gap="xs" mb="md">
                <IconCategory size={16} color="#888" />
                {video.categories.map((category) => (
                  <L2KBadge key={category.id} variant="outline" size="md">
                    {category.name}
                  </L2KBadge>
                ))}
              </Group>
            )}

            {/* Tags */}
            {video.tags && video.tags.length > 0 && (
              <Group gap="xs" mb="md">
                {video.tags.map((tag, index) => (
                  <L2KBadge key={index} variant="muted" size="sm">
                    {tag}
                  </L2KBadge>
                ))}
              </Group>
            )}

            {/* Description */}
            {video.description && (
              <Box
                p="md"
                style={{
                  background: "rgba(255, 255, 255, 0.03)",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                <L2KH5 mb="xs">About this video</L2KH5>
                <L2KText variant="secondary" style={{ whiteSpace: "pre-wrap" }}>
                  {video.description}
                </L2KText>
              </Box>
            )}

            {/* Learning Tracks */}
            {video.learning_tracks && video.learning_tracks.length > 0 && (
              <Box mt="md">
                <L2KCaption variant="tertiary" mb="xs">
                  Part of learning tracks:
                </L2KCaption>
                <Group gap="xs">
                  {video.learning_tracks.map((track, index) => (
                    <L2KBadge key={index} variant="dot" size="md">
                      {track}
                    </L2KBadge>
                  ))}
                </Group>
              </Box>
            )}
          </Box>

          {/* Related Videos */}
          {related_videos && related_videos.length > 0 && (
            <>
              <Divider
                my="xl"
                color="rgba(255, 255, 255, 0.1)"
                label={<L2KH4>Related Videos</L2KH4>}
                labelPosition="left"
              />
              <Grid>
                {related_videos.map((relatedVideo) => (
                  <Grid.Col
                    key={relatedVideo.id}
                    span={{ base: 12, xs: 6, sm: 4, md: 4 }}
                  >
                    <VideoCard
                      video={relatedVideo}
                      userVideo={null}
                      onVideoClick={handleRelatedVideoClick}
                    />
                  </Grid.Col>
                ))}
              </Grid>
            </>
          )}
      </Stack>
    </PageContainer>
  );

  return (
    <>
      <SEO
        title={`${video.title} - Learntokiz`}
        description={
          video.description || `Learn ${video.title} with Learntokiz`
        }
        image={video.thumbnail_url}
        url={`https://learntokiz.io/videos/${video.slug || video.id}`}
        type="video"
      />
      {coming_soon ? (
        <ComingSoonPageOverlay>{pageBody}</ComingSoonPageOverlay>
      ) : (
        pageBody
      )}
    </>
  );
}

// Per-page layout (Inertia pattern)
VideoShow.layout = (page: React.ReactNode) => (
  <HybridLayout hideOnScroll={false}>{page}</HybridLayout>
);
